import { setLightPaint } from "./theme";
import type { Map as MlMap, GeoJSONSource } from "maplibre-gl";

/**
 * v0.9.7 — the same calendar day in 2554 (2011), from the RID Reservoir API (CC BY, data.go.th
 * big_dams_public) fetched once for 1 Sep – 30 Nov 2011 (scripts/fetch_dams_2011.mjs →
 * /data/dams-2011.json). Owner's rules:
 *  - the main comparison is the VOLUME each year reported (never back-computed from a %)
 *  - % of BOTH years = volume ÷ the capacity the app uses today (SWOC basis), stated under the table;
 *    when the API's own 2011 % differs from that by > 2 points → "ระบบเดิมของกรมชลฯ คิดจากฐานต่างกัน (X%)"
 *  - inflow / release: the daily values compared as they are
 *  - C.13: only "สูงสุดปี 2554: 3,726 ลบ.ม./วิ (21 ก.ย. 2554)" (Sing Buri CSV, Open Data Common) — no daily
 *  - no 2011 volume for that dam/day → no comparison section at all
 *  - neutral wording: numbers only ("มากกว่า/น้อยกว่า … อยู่ X"), never safer/worse/"เหมือนปี 54"
 * This year's line in the small chart = what history/dams.jsonl has (→ /data/dam-history.json at build).
 */
interface Day11 { volumeMcm: number | null; percentRid: number | null; inflowMcmDay: number | null; outflowMcmDay: number | null }
interface Dam11 { ridName: string; ridId: number; ridStorageMcm: number | null; days: Record<string, Day11> }
interface File11 {
  source: string; license: string; licenseUrl: string; urlTemplate: string; fetchedAt: string;
  dams: Record<string, Dam11>;
  c13Max2011?: { cms: number; date: string; station: string; source: string; license: string; url: string; datasetUrl: string };
}
interface History { dams: Record<string, [string, number][]> } // [valueDate, volumeMcm] this year

export interface DamToday {
  name: string; capacityMcm: number | null; volumeMcm: number | null; inflowMcmDay: number | null; outflowMcmDay: number | null; valueDate?: string;
  percent?: number | null; baseMismatch?: unknown; // the % the card shows (SWOC / RID) + the v0.9.8a base guard
}

/** v0.9.8a: the app's base must reproduce the % the card shows (±2 points) — otherwise the 2554 table
 *  would put a second, conflicting % for "today" on the page → hidden until the base is fixed. */
export function baseOk(d: DamToday): boolean {
  if (d.baseMismatch) return false;
  if (d.percent == null || d.volumeMcm == null || !d.capacityMcm) return false;
  return Math.abs((d.volumeMcm / d.capacityMcm) * 100 - d.percent) <= 2;
}

let f11: File11 | null = null;
let hist: History | null = null;
let loading: Promise<void> | null = null;

export function loadDam2011(): Promise<void> {
  loading ??= (async () => {
    try { const r = await fetch("/data/dams-2011.json"); if (r.ok) f11 = (await r.json()) as File11; } catch { /* no file → no section */ }
    try { const r = await fetch("/data/dam-history.json"); if (r.ok) hist = (await r.json()) as History; } catch { /* chart shows 2554 only */ }
  })();
  return loading;
}

const MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const thDay = (iso: string): string => { const [y, m, d] = iso.split("-").map(Number); return `${d} ${MONTHS[m! - 1]} ${String(y! + 543).slice(2)}`; };
const thDayFull = (iso: string): string => { const [y, m, d] = iso.split("-").map(Number); return `${d} ${MONTHS[m! - 1]} ${y! + 543}`; };
const fmt = (n: number, dp = 0): string => n.toLocaleString("th-TH", { minimumFractionDigits: dp, maximumFractionDigits: dp });
const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));

/** The 2011 record of the same day-month as `valueDate` (null outside 1 Sep–30 Nov or not reported). */
export function sameDay2011(name: string, valueDate: string | undefined): { date: string; day: Day11; dam: Dam11 } | null {
  if (!f11 || !valueDate) return null;
  const dam = f11.dams[name]; if (!dam) return null;
  const date = `2011-${valueDate.slice(5, 10)}`;
  const day = dam.days[date];
  return day && day.volumeMcm != null ? { date, day, dam } : null;
}

/** "มากกว่า/น้อยกว่า … อยู่ X" from today's side — numbers only. */
function diffText(today: number, then: number, unit: string, dp: number): string {
  const d = today - then;
  if (Math.abs(d) < Math.pow(10, -dp) / 2) return "เท่ากัน";
  return `${d > 0 ? "มากกว่า" : "น้อยกว่า"} ${fmt(Math.abs(d), dp)}${unit ? ` ${unit}` : ""}`;
}

/** Dam card section "เทียบวันเดียวกันปี 2554" (empty string when there is nothing to compare). */
export function compare2011Html(d: DamToday): string {
  const m = sameDay2011(d.name, d.valueDate);
  if (!m || d.volumeMcm == null || !baseOk(d)) return "";
  const y = m.day, cap = d.capacityMcm;
  const rows: string[] = [];
  const row = (label: string, a: string, b: string, diff: string): void => { rows.push(`<span>${label}</span><b>${a}</b><b>${b}</b><span class="muted">${diff}</span>`); };
  row("ปริมาตร (ล้าน ลบ.ม.)", fmt(d.volumeMcm, 0), fmt(y.volumeMcm!, 0), diffText(d.volumeMcm, y.volumeMcm!, "", 0));
  let note = "";
  if (cap != null && cap > 0) {
    // whole-number % — the same figure the card head / map show for today (e.g. 110.7 → 111%)
    const pa = Math.round((d.volumeMcm / cap) * 100), pb = Math.round((y.volumeMcm! / cap) * 100);
    row("% ของความจุ", `${pa}%`, `${pb}%`, diffText(pa, pb, "จุด", 0));
    note = `คิดจากความจุที่กรมชลประทานใช้คิด % (${fmt(cap, cap % 1 ? 1 : 0)} ล้าน ลบ.ม.) ทั้งสองปี`;
    if (y.percentRid != null && Math.abs(y.percentRid - (y.volumeMcm! / cap) * 100) > 2) note += ` · ปี 2554: ระบบเดิมของกรมชลฯ คิดจากฐานต่างกัน (${fmt(y.percentRid, 1)}%)`;
  }
  const flow = (label: string, a: number | null, b: number | null): void => {
    if (a == null && b == null) return;
    row(label, a == null ? `<span class="muted">ยังไม่มีรายงาน</span>` : fmt(a, 2), b == null ? `<span class="muted">ไม่มีรายงาน</span>` : fmt(b, 2),
      a != null && b != null ? diffText(a, b, "", 2) : "");
  };
  flow("ไหลเข้า (ล้าน ลบ.ม./วัน)", d.inflowMcmDay, y.inflowMcmDay);
  flow("ระบาย (ล้าน ลบ.ม./วัน)", d.outflowMcmDay, y.outflowMcmDay);
  const url = f11!.urlTemplate.replace("{date}", m.date);
  return `<div class="y54" id="y54">
    <div class="y54-h">📅 เทียบวันเดียวกันปี 2554</div>
    <div class="muted small">ข้อมูล ${thDayFull(d.valueDate!)} | ${thDayFull(m.date)}</div>
    <div class="y54-grid"><span></span><span class="muted small">ข้อมูล ${thDayFull(d.valueDate!)}</span><span class="muted small">${thDayFull(m.date)}</span><span class="muted small">ส่วนต่าง</span>${rows.join("")}</div>
    ${note ? `<div class="muted small">${esc(note)}</div>` : ""}
    ${sparkline(d.name, d.valueDate!)}
    <div class="muted small">ปี 2554: ${esc(f11!.source)} · <a href="${url}" target="_blank" rel="noopener">ข้อมูล ${thDay(m.date)} ↗</a> · ${esc(f11!.license)}</div>
  </div>`;
}

/** Small chart: volume 1 Sep – 30 Nov, 2554 (grey) vs this year as far as history has it (cyan). */
function sparkline(name: string, valueDate: string): string {
  const dam = f11?.dams[name]; if (!dam) return "";
  const start = Date.UTC(2011, 8, 1), DAY = 86400000, N = 91;
  const a: (number | null)[] = Array.from({ length: N }, (_, i) => dam.days[new Date(start + i * DAY).toISOString().slice(0, 10)]?.volumeMcm ?? null);
  const yr = Number(valueDate.slice(0, 4));
  const b: (number | null)[] = Array(N).fill(null);
  for (const [dt, v] of hist?.dams[name] ?? []) {
    if (!dt.startsWith(String(yr))) continue;
    const i = Math.round((Date.UTC(yr, Number(dt.slice(5, 7)) - 1, Number(dt.slice(8, 10))) - Date.UTC(yr, 8, 1)) / DAY);
    if (i >= 0 && i < N) b[i] = v;
  }
  const vals = [...a, ...b].filter((v): v is number => v != null);
  if (vals.length < 2) return "";
  const lo = Math.min(...vals), hi = Math.max(...vals), W = 260, H = 54, pad = 3;
  const x = (i: number): number => pad + (i / (N - 1)) * (W - 2 * pad);
  const yv = (v: number): number => H - pad - ((v - lo) / Math.max(1e-9, hi - lo)) * (H - 2 * pad);
  const path = (s: (number | null)[]): string => { let p = "", pen = false; s.forEach((v, i) => { if (v == null) { pen = false; return; } p += `${pen ? "L" : "M"}${x(i).toFixed(1)},${yv(v).toFixed(1)}`; pen = true; }); return p; };
  const dots = b.map((v, i) => (v == null ? "" : `<circle cx="${x(i).toFixed(1)}" cy="${yv(v).toFixed(1)}" r="2.2" fill="#00d4ff"/>`)).join("");
  return `<svg class="y54-spark" viewBox="0 0 ${W} ${H}" width="100%" height="${H}" role="img" aria-label="ปริมาตรน้ำ 1 ก.ย.–30 พ.ย. ปี 2554 เทียบปีนี้">
    <path d="${path(a)}" fill="none" stroke="#8a97a8" stroke-width="1.6"/><path d="${path(b)}" fill="none" stroke="#00d4ff" stroke-width="2"/>${dots}</svg>
    <div class="y54-legend muted small"><span class="k k54"></span>ปี 2554 <span class="k know"></span>ปี ${yr + 543} (เท่าที่มีบันทึก) · ปริมาตร ${fmt(lo, 0)}–${fmt(hi, 0)} ล้าน ลบ.ม. · 1 ก.ย.–30 พ.ย.</div>`;
}

/** C.13: today vs the 2554 annual peak (Sing Buri CSV) — no daily comparison. */
export function c13Max2011Html(todayCms: number | null): string {
  const c = f11?.c13Max2011; if (!c) return "";
  return `<div class="y54 y54-c13"><div>${todayCms != null ? `วันนี้ ${fmt(todayCms)} ลบ.ม./วิ · ` : ""}สูงสุดปี 2554: <b>${fmt(c.cms)} ลบ.ม./วิ</b> (${thDayFull(c.date)})</div>
    <div class="muted small">ที่มา: ${esc(c.source)} · <a href="${c.datasetUrl}" target="_blank" rel="noopener">data.go.th ↗</a> · ${esc(c.license)}</div></div>`;
}

/** One short summary line for the upstream dam (not a reason for the summary level). */
export function summaryLine2011(d: DamToday): string | null {
  const m = sameDay2011(d.name, d.valueDate);
  if (!m || d.volumeMcm == null || !baseOk(d)) return null;
  return `ปริมาตร ข้อมูล ${thDayFull(d.valueDate!)} ${fmt(d.volumeMcm, 0)} | ${thDayFull(m.date)} ${fmt(m.day.volumeMcm!, 0)} ล้าน ลบ.ม. (${diffText(d.volumeMcm, m.day.volumeMcm!, "", 0)})`;
}

/* ── country view: "เทียบปี 54" toggle → "ปี 54: X%" under each dam's % ─────────── */
const SRC = "dams-y54", LAYER = "dams-y54-label";
export function setY54Labels(map: MlMap, on: boolean, dams: DamToday[], at: (name: string) => [number, number] | null): void {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
    setLightPaint(LAYER, { "text-color": "#1f2937", "text-halo-color": "#ffffff", "text-halo-width": 2 }); // v0.9.9a: dark on white in light mode
    map.addLayer({ id: LAYER, type: "symbol", source: SRC, minzoom: 4, layout: {
      "text-field": ["get", "t"], "text-font": ["Noto Sans Regular"], "text-size": 11, "text-anchor": "top", "text-offset": [0, 3.05], // under name + value (v0.9.10a)
      "text-allow-overlap": true, "text-ignore-placement": false,
    }, paint: { "text-color": "#b9c6d6", "text-halo-color": "#05080f", "text-halo-width": 1.6 } });
  }
  map.setLayoutProperty(LAYER, "visibility", on ? "visible" : "none");
  if (!on) return;
  const feats: GeoJSON.Feature[] = [];
  for (const d of dams) {
    const m = sameDay2011(d.name, d.valueDate), p = at(d.name);
    if (!m || !p || d.capacityMcm == null || d.capacityMcm <= 0 || !baseOk(d)) continue;
    feats.push({ type: "Feature", properties: { t: `ปี 54: ${Math.round((m.day.volumeMcm! / d.capacityMcm) * 100)}%` }, geometry: { type: "Point", coordinates: p } });
  }
  (map.getSource(SRC) as GeoJSONSource).setData({ type: "FeatureCollection", features: feats });
}
